<template>
  <div class="collapsible-section" :class="{ 'has-error': hasErrors }">
    <div class="header" @click="toggle">
      <div>
        <strong>Service Type <span class="required-asterisk">*</span></strong>
        <!-- <div class="desc">Tell us what documents and qualifications are required from support workers.</div> -->
      </div>
      <v-btn icon variant="text" class="chevron-btn">
        <v-icon>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </v-btn>
    </div>

    <v-expand-transition>
      <div v-show="expanded || (services_offered?.length || 0) > 0">
        <v-form class="support-requirements-needed">
          <AppMultiTagSearch
            v-model="services_offered"
            data-testid="services_offered"
            :items="servicesNewList"
            :loading="servicesNewListLoading"
            :disabled="disabled"
            :errors="errors.services"
            item-value="id"
            item-title="name"
            return-object
            hide-details="auto"
            placeholder="Select support services"
            @search="filterServices"
            @remove="removeServices"
          />
        </v-form>
      </div>
    </v-expand-transition>
    <!-- Display validation errors outside the collapsible section -->
    <div v-if="hasErrors && !expanded" class="validation-errors">
        <div v-for="error in errorMessages" :key="error" class="error-message">
          {{ error }}
        </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { ref, computed } from 'vue';

import { AppMultiTagSearch } from '@/core/components';
import { supportRequirementServiceWithSearch } from '@/modules/auth/services/SupportRequirementService';
import type { Service } from '@/types/Service';

// Props and defaults
const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    errors?: {
      services?: any[];
    };
  }>(),
  {
    disabled: false,
    errors: () => ({}),
  }
);

// UI State
const expanded = ref(false);
const toggle = () => (expanded.value = !expanded.value);

// Service Data Model
const services_offered = defineModel<Service[]>('services_offered');

// Compute validation error state
const hasErrors = computed(() => {
  return props.errors?.services && props.errors.services.length > 0;
});

const errorMessages = computed(() => {
  if (!props.errors?.services) return [];
  return props.errors.services.map((error: any) => error.$message || error.message || 'This field is required');
});

// Search Handling
const filterServices = (query: string) => {
  getServicesNew(query);
};

function removeServices(servicesNew: { name: string; id: number }) {
  const index = servicesNewList.value.findIndex((item) => item.id === servicesNew.id);
  servicesNewList.value.splice(index, 1);
}

// For debounced search
const servicesNewList = ref<Service[]>([]);
// const servicesNewList = ref({ data: [] });

const servicesNewListLoading = ref(false);

const debouncedGetServicesNew = useDebounceFn(async (search: string) => {
  servicesNewListLoading.value = true;
  const { data } = await supportRequirementServiceWithSearch.getSupportRequirementsWithSearchAndPagination({
    search,
    page: 1,
    per_page: 20,
  });
  servicesNewList.value = data.value || [];
  servicesNewListLoading.value = false;
}, 500);

function getServicesNew(value: string) {
  if (!value) return;
  debouncedGetServicesNew(value);
}

onMounted(async () => {
  await nextTick(async () => {
    const initialSearch = 'a';
    await Promise.all([getServicesNew(initialSearch)]);
  });
});
</script>

<style scoped>
.collapsible-section {
  margin-bottom: 2rem;
  background: #fafafa;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  padding: 1.5rem;
}

.collapsible-section.has-error {
  border: 1px solid #ff5252;
  background: #fff5f5;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}
.chevron-btn {
  margin-left: 1rem;
}
.desc {
  font-size: 0.95em;
  color: #555;
}
.validation-errors {
  margin-top: 0.5rem;
}
.error-message {
  color: red;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}
.required-asterisk {
  color: #f44336 !important;
}
</style>
